import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Database,
  Repeat,
  ShieldCheck,
  Clock,
  PieChart,
  BarChart3,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { fetchWithAuth } from "../../utils/api";
import { formatUSD, formatCount } from "../../utils/formatAmount";
import { TokenLogo } from "../common/CryptoLogo";
import "./TransactionSummary.css";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

// "all" is the default: a merchant opening Transactions sees their whole
// history first and narrows it from there.
const RANGES = [
  { key: "all", label: "All" },
  { key: "7d", label: "7D" },
  { key: "30d", label: "30D" },
  { key: "90d", label: "90D" },
];

/** Seconds -> "2h 38m" / "49s". Returns null when there is nothing to show. */
const formatDuration = (secs) => {
  if (secs == null || !Number.isFinite(secs)) return null;
  if (secs < 60) return `${Math.round(secs)}s`;
  const m = Math.round(secs / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
};

/**
 * Smooth sparkline through the daily series.
 *
 * Drawn with a Catmull-Rom-ish midpoint curve rather than straight segments —
 * the figure is a trend, and sharp joints read as precision the daily buckets
 * do not have.
 */
const Sparkline = ({ points, tone }) => {
  const shape = useMemo(() => {
    if (!points || points.length < 2) return null;
    const w = 100;
    const h = 40;
    const max = Math.max(...points, 0);
    // Baseline at zero rather than at the smallest value. Scaling to the range
    // made a flat week of real volume look identical to a flat week of nothing,
    // and exaggerated trivial wobble into a mountain range.
    const span = max || 1;
    const top = 6;
    const bottom = h - 4;
    const xy = points.map((v, i) => [
      (i / (points.length - 1)) * w,
      bottom - (Math.max(v, 0) / span) * (bottom - top),
    ]);

    let line = `M ${xy[0][0]},${xy[0][1]}`;
    for (let i = 1; i < xy.length; i += 1) {
      const [px, py] = xy[i - 1];
      const [cx, cy] = xy[i];
      const mx = (px + cx) / 2;
      line += ` C ${mx},${py} ${mx},${cy} ${cx},${cy}`;
    }
    // Closed copy of the same curve, for the tint beneath it.
    const area = `${line} L ${w},${h} L 0,${h} Z`;
    return { line, area };
  }, [points]);

  if (!shape) return null;
  return (
    <svg className="txs-spark" viewBox="0 0 100 40" preserveAspectRatio="none" aria-hidden="true">
      <path d={shape.area} className={`txs-spark__area txs-spark__area--${tone}`} />
      <path d={shape.line} className={`txs-spark__line txs-spark__line--${tone}`} />
    </svg>
  );
};

/** One headline figure. `delta` is null when there is no comparable period. */
const SummaryCard = ({ icon: Icon, tone, label, value, delta, deltaGoodWhenDown, series, rangeLabel, isAllTime, loading }) => {
  // A lower number is an improvement for payment time and a regression for
  // everything else, so the colour cannot be driven by the sign alone.
  const improving = delta == null ? null : deltaGoodWhenDown ? delta < 0 : delta > 0;

  return (
    <div className={`txs-card txs-card--${tone}`}>
      <span className="txs-card__icon" aria-hidden="true">
        <Icon size={16} />
      </span>
      <div className="txs-card__body">
        <span className="txs-card__label">{label}</span>
        <span className="txs-card__value">{loading ? "—" : value}</span>
        {isAllTime ? null : !loading && delta != null ? (
          <span className={`txs-card__delta ${improving ? "is-up" : "is-down"}`}>
            {delta < 0 ? <ArrowDown size={13} /> : <ArrowUp size={13} />}
            {Math.abs(delta).toFixed(1)}%
          </span>
        ) : (
          // Said plainly rather than shown as 0%: no prior period is not "no change".
          <span className="txs-card__delta is-flat">{loading ? "" : "no prior period"}</span>
        )}
        {/* All time has no earlier period to compare against, so it says what
            the figure covers instead of "no prior period". */}
        <span className="txs-card__sub">{isAllTime ? "All time" : `vs. previous ${rangeLabel}`}</span>
      </div>
      {!loading && <Sparkline points={series} tone={tone} />}
    </div>
  );
};

const TransactionSummary = ({ token, mode, filters = {} }) => {
  const [range, setRange] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const {
    type,
    search,
    merchantId,
    network,
    currency,
  } = filters;

  const fetchSummary = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ range });
      params.append("isTestnet", mode === "test" ? "true" : "false");
      // Mirrors the list exactly, so the cards describe the rows on screen.
      params.append("hideDust", "true");
      params.append("hideUntrackedUnsupported", "true");
      if (type && type !== "all") params.append("type", type);
      if (search) params.append("search", search);
      if (merchantId && merchantId !== "all") params.append("merchantId", merchantId);
      if (network && network !== "all") params.append("network", network);
      if (currency && currency !== "all") params.append("currency", currency);

      const response = await fetchWithAuth(`${API_BASE_URL}/transactions/summary?${params}`);
      const json = await response.json();
      if (json.success) setData(json.data);
    } catch (error) {
      console.error("Failed to fetch transaction summary:", error);
    } finally {
      setLoading(false);
    }
  }, [token, mode, range, type, search, merchantId, network, currency]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const current = data?.current;
  const daily = data?.daily || [];
  const assets = data?.assets || [];
  // Read from the selection, not the response, so the wording switches the
  // moment a button is pressed instead of after the fetch lands.
  const isAllTime = range === "all";
  const rangeLabel = `${data?.days || 7} days`;

  const volumeSeries = daily.map((d) => d.volumeUsd);
  const countSeries = daily.map((d) => d.count);
  const peakDay = Math.max(...daily.map((d) => d.volumeUsd), 0);

  // Only the last bucket is "today"; highlighting any other would be wrong.
  const todayIndex = daily.length - 1;

  return (
    <div className="txs">
      <div className="txs-cards">
        <SummaryCard
          icon={Database}
          tone="violet"
          label="Total Volume"
          value={`$${formatUSD(current?.volumeUsd || 0)}`}
          delta={data?.deltas?.volumeUsd ?? null}
          series={volumeSeries}
          rangeLabel={rangeLabel}
          isAllTime={isAllTime}
          loading={loading}
        />
        <SummaryCard
          icon={Repeat}
          tone="blue"
          label="Transactions"
          value={formatCount(current?.count || 0)}
          delta={data?.deltas?.count ?? null}
          series={countSeries}
          rangeLabel={rangeLabel}
          isAllTime={isAllTime}
          loading={loading}
        />
        <SummaryCard
          icon={ShieldCheck}
          tone="green"
          label="Success Rate"
          value={current?.successRate != null ? `${current.successRate.toFixed(1)}%` : "—"}
          delta={data?.deltas?.successRate ?? null}
          series={countSeries}
          // The daily series carries no per-day success figure, so this shows
          // daily volume of attempts. Labelled honestly in the sub-line.
          rangeLabel={rangeLabel}
          isAllTime={isAllTime}
          loading={loading}
        />
        <SummaryCard
          icon={Clock}
          tone="violet"
          label="Avg. Payment Time"
          value={formatDuration(current?.avgPaymentSeconds) || "—"}
          delta={data?.deltas?.avgPaymentSeconds ?? null}
          // Faster is better, so a fall here is the good direction.
          deltaGoodWhenDown
          series={volumeSeries}
          rangeLabel={rangeLabel}
          isAllTime={isAllTime}
          loading={loading}
        />
      </div>

      <div className="txs-panels">
        <section className="txs-panel txs-panel--assets">
          <header className="txs-panel__head">
            <h3>
              <PieChart size={15} />
              Top Assets
            </h3>
          </header>
          {assets.length === 0 ? (
            <p className="txs-empty">
              {loading
                ? "Loading…"
                : isAllTime
                  ? "No priced transactions yet"
                  : `No priced transactions in the last ${rangeLabel}`}
            </p>
          ) : (
            <div className="txs-assets">
              {assets.map((a) => (
                <div className="txs-asset" key={a.symbol}>
                  <TokenLogo symbol={a.symbol} size={30} />
                  <div className="txs-asset__body">
                    <div className="txs-asset__top">
                      <span className="txs-asset__sym">{a.symbol}</span>
                    </div>
                    <span className="txs-asset__usd">${formatUSD(a.volumeUsd)}</span>
                    <span className="txs-asset__bar" aria-hidden="true">
                      {/* A held asset always shows a sliver, so "a little" never
                          renders identically to "none". */}
                      <span
                        className="txs-asset__fill"
                        style={{ width: `${a.volumeUsd > 0 ? Math.max(a.share, 2) : 0}%` }}
                      />
                    </span>
                    <span className="txs-asset__count">
                      {formatCount(a.count)} {a.count === 1 ? "transaction" : "transactions"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="txs-panel txs-panel--activity">
          <header className="txs-panel__head">
            <h3>
              <BarChart3 size={15} />
              Activity Overview
            </h3>
            <div className="txs-range" role="group" aria-label="Date range">
              {RANGES.map((r) => (
                <button
                  key={r.key}
                  type="button"
                  className={`txs-range__btn ${range === r.key ? "active" : ""}`}
                  onClick={() => setRange(r.key)}
                  aria-pressed={range === r.key}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </header>

          <div
            // Past a month of bars the fixed gaps alone outgrow the panel ("all"
            // can return up to 180 days), so dense series pack tighter.
            className={`txs-chart ${daily.length > 31 ? "txs-chart--dense" : ""}`}
            role="img"
            aria-label={isAllTime ? "Daily volume, all time" : `Daily volume over the last ${rangeLabel}`}
          >
            {daily.map((d, i) => {
              const pct = peakDay > 0 ? (d.volumeUsd / peakDay) * 100 : 0;
              return (
                <span
                  className={`txs-bar ${i === todayIndex ? "is-today" : ""}`}
                  key={d.date}
                  title={`${d.date} · $${formatUSD(d.volumeUsd)} · ${d.count} txn`}
                >
                  <span className="txs-bar__track">
                    <span
                      className="txs-bar__fill"
                      style={{ height: `${d.volumeUsd > 0 ? Math.max(pct, 6) : 0}%` }}
                    />
                  </span>
                  {/* Only label every bar on the short range; 90 labels is noise. */}
                  {daily.length <= 14 && (
                    <em className="txs-bar__day">
                      {new Date(`${d.date}T00:00:00`).toLocaleDateString(undefined, {
                        weekday: "narrow",
                      })}
                    </em>
                  )}
                </span>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
};

export default TransactionSummary;
